import React, { useEffect, useRef, useState } from 'react';
import { checkUnmeContractPayoutResult } from '../api/cpSettlement';
import { hasUnmappedUnmeContract } from '../api/unmePayout';
import { unmeContractColor } from '../../Common/utils/unmeContractColor';

// Prevent repeated result lookups for the same payout while a request is pending.
const activePayoutChecks = new Set();

const statusLabels = {
  SUCCESS: '지급 완료',
  DECLINED: '지급 실패',
  UNKNOWN: '결과 확인 필요',
  SENDING: '지급 처리 중',
  NOT_REQUESTED: '지급 요청 전',
  NOT_REQUIRED: '지급 대상 없음',
};

const formatAmount = (value) => {
  if (value == null || value === '') return '금액 확인 필요';
  if (typeof value === 'number' && (!Number.isFinite(value) || Math.abs(value) > Number.MAX_SAFE_INTEGER)) return '금액 확인 필요';
  const raw = String(value).trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(raw)) return '금액 확인 필요';
  const [integer, fraction] = raw.split('.');
  const decimal = fraction?.replace(/0+$/, '');
  return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${decimal ? `.${decimal}` : ''}원`;
};

/** Inline contract data supplied by scheduled/transfers; absent data is handled by the existing UI. */
export default function UnmeScheduledContracts({ contractSettlement, settlementId, adminRole = '', onResultChecked, onRetry, retryEnabled = false, retryAmount = null, retryBlocked = false }) {
  const [checkingId, setCheckingId] = useState('');
  const [notice, setNotice] = useState('');
  const [confirmRetry, setConfirmRetry] = useState(false);
  const checkingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  if (contractSettlement?.available !== true) return null;
  const contracts = Array.isArray(contractSettlement.contracts) ? contractSettlement.contracts : [];
  const hasUnmapped = hasUnmappedUnmeContract(contractSettlement);
  const visibleContracts = contracts.filter((contract) => !hasUnmappedUnmeContract(contract));
  const retryOffered = adminRole === 'SUPER' && contractSettlement.canRetry === true &&
    contractSettlement.reconciled === true && contracts.some((contract) =>
      contract?.payoutStatus === 'DECLINED' && contract?.canRetry === true);

  const checkResult = async (payoutId) => {
    const key = String(payoutId ?? '');
    if (adminRole !== 'SUPER' || !/^[1-9]\d*$/.test(key) || checkingRef.current || activePayoutChecks.has(key)) return;
    checkingRef.current = true;
    activePayoutChecks.add(key);
    setCheckingId(key);
    setNotice('');
    try {
      await checkUnmeContractPayoutResult(key);
      if (mountedRef.current) setNotice('조회 요청을 완료했습니다. 최신 지급 상태를 확인해 주세요.');
    } catch (_) {
      if (mountedRef.current) setNotice('지급 결과를 확인하지 못했습니다. 관리자에게 확인해 주세요.');
    } finally {
      activePayoutChecks.delete(key);
      checkingRef.current = false;
      if (mountedRef.current) {
        setCheckingId('');
        if (onResultChecked) onResultChecked();
      }
    }
  };

  return <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs" onClick={(event) => event.stopPropagation()}>
    {contractSettlement.hasPartialSuccess === true && <p className="mb-1 font-semibold text-amber-700">일부 지급 완료</p>}
    {contractSettlement.reconciled !== true && <p role="status" className="mb-1 font-semibold text-amber-700">계약별 집계 확인 필요</p>}
    {hasUnmapped && <p role="status" className="mb-1 font-semibold text-amber-700">계약 정보 확인 필요</p>}
    {!contracts.length ? <p className="text-amber-700">계약별 집계 확인 필요</p> : <div className="divide-y divide-slate-200">
      {visibleContracts.map((contract, index) => {
        const status = String(contract?.payoutStatus || '').toUpperCase();
        const keyIsNull = contract?.contractKey === null || (contract?.contractKey === undefined && contract?.contract_key === null);
        const name = keyIsNull ? '-' : (typeof contract?.contractName === 'string' && contract.contractName.trim()) || '-';
        const payoutId = String(contract?.latestPayout?.id ?? '');
        const payoutMatchesContract = contract?.id != null && contract?.latestPayout?.contract_id != null &&
          String(contract.id) === String(contract.latestPayout.contract_id) &&
          String(contract.latestPayout.status || '').toUpperCase() === status &&
          /^[1-9]\d*$/.test(String(settlementId ?? '')) &&
          String(contract.settlement_id ?? '') === String(settlementId) &&
          String(contract.latestPayout.settlement_id ?? '') === String(settlementId);
        const canCheck = adminRole === 'SUPER' && ['UNKNOWN', 'SENDING'].includes(status) && payoutMatchesContract && /^[1-9]\d*$/.test(payoutId);
        const statusLabel = statusLabels[status] || '상태 확인 필요';
        return <div key={`${contract?.id ?? 'unknown'}-${index}`} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 py-1.5">
          <span className="min-w-0 text-slate-700"><span className={`inline-flex rounded border px-1.5 py-0.5 font-semibold ${unmeContractColor(name === '-' ? null : (contract?.contractKey ?? contract?.contract_key))}`}>{name}</span> <span className={['UNKNOWN', 'DECLINED'].includes(status) ? 'text-amber-700' : 'text-slate-500'}>· {statusLabel}</span></span>
          <span className="ml-auto flex items-center gap-2"><strong className="tabular-nums text-slate-800">{formatAmount(contract?.net_amount)}</strong>
            {canCheck && <button type="button" disabled={Boolean(checkingId)} onClick={() => checkResult(payoutId)} className="rounded border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-700 disabled:opacity-50" aria-label={`${name} 지급 결과 다시 확인`}>{checkingId === payoutId ? '조회 중…' : '결과 다시 확인'}</button>}
          </span>
          {status === 'UNKNOWN' && !contract?.latestPayout?.transaction_id && <p className="w-full text-slate-500">자동 결과 확인이 어려울 수 있어 관리자 확인이 필요합니다.</p>}
        </div>;
      })}
    </div>}
    {notice && <p role="status" className="mt-1 text-slate-600">{notice}</p>}
    {retryBlocked && <p role="status" className="mt-1 font-semibold text-amber-700">재신청 전 최신 지급 상태 확인이 필요합니다.</p>}
    {retryOffered && <div className="mt-2 border-t border-slate-200 pt-2">
      {!confirmRetry ? <button type="button" disabled={hasUnmapped || !retryEnabled || typeof onRetry !== 'function'} onClick={() => setConfirmRetry(true)} className="rounded border border-blue-300 bg-white px-2 py-1 font-semibold text-blue-700 disabled:cursor-not-allowed disabled:opacity-50">실패 건 재신청</button> : <div role="group" aria-label="실패 건 재신청 확인" className="space-y-2">
        <p className="font-semibold text-slate-800">재신청 예상 금액 {formatAmount(retryAmount)}</p>
        <p className="text-slate-600">이 정산의 실패 건을 함께 요청합니다. 이미 지급 완료된 계약은 제외됩니다.</p>
        <div className="flex gap-2"><button type="button" onClick={() => setConfirmRetry(false)} className="rounded border border-slate-300 bg-white px-2 py-1">취소</button>
          <button type="button" disabled={hasUnmapped || !retryEnabled || typeof onRetry !== 'function'} onClick={() => { setConfirmRetry(false); onRetry(); }} className="rounded bg-blue-600 px-2 py-1 font-semibold text-white disabled:opacity-50">재신청 확인</button></div>
      </div>}
    </div>}
  </div>;
}
